import { useEffect, useState } from 'react';
import {
  auctionService,
  type AuctionItem,
  type AuctionListItem,
  type AuctionLotItem,
  type AuctionStatus,
  type AuctionType,
  type CreateAuctionRequest,
  type CreateAuctionLotRequest,
} from '../../services/auction.service';
import './AdminAuction.css';

type FilterStatus = 'all' | AuctionStatus;

const STATUS_LABELS: Record<AuctionStatus, string> = {
  Upcoming: 'Предстоящий',
  Active: 'Идёт',
  Completed: 'Завершён',
};

const TYPE_LABELS: Record<AuctionType, string> = {
  Offline: 'Офлайн',
  Online: 'Онлайн',
  Hybrid: 'Гибридный',
};

const emptyAuctionForm: CreateAuctionRequest = {
  titleKz: '',
  titleRu: '',
  descriptionKz: '',
  descriptionRu: '',
  farmNameKz: '',
  farmNameRu: '',
  regionKz: '',
  regionRu: '',
  date: '',
  auctionType: 'Offline',
  breed: '',
  status: 'Upcoming',
  youtubeUrl: '',
  youtubeEmbedUrl: '',
  coverImage: '',
  bannerImage: '',
};

/** Форма лота: числовые поля храним строками, конвертируем при отправке. */
interface LotFormState {
  lotNumber: string;
  name: string;
  inj: string;
  birthDate: string;
  ageMonths: string;
  birthWeight: string;
  weaningWeight: string;
  yearlingWeight: string;
  currentWeight: string;
  averageDailyGain: string;
  scrotalCircumference: string;
  spermQualityScore: string;
  heightAtWithers: string;
  line: string;
  startingPrice: string;
  imageUrl: string;
}

const emptyLotForm: LotFormState = {
  lotNumber: '',
  name: '',
  inj: '',
  birthDate: '',
  ageMonths: '',
  birthWeight: '',
  weaningWeight: '',
  yearlingWeight: '',
  currentWeight: '',
  averageDailyGain: '',
  scrotalCircumference: '',
  spermQualityScore: '',
  heightAtWithers: '',
  line: '',
  startingPrice: '',
  imageUrl: '',
};

const toNumberOrNull = (v: string): number | null => {
  const t = v.trim();
  if (!t) return null;
  const n = Number(t.replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

const formatDate = (iso: string) => {
  const [y, m, d] = iso.split('-');
  return y && m && d ? `${d}.${m}.${y}` : iso;
};

const formatPrice = (v: number) => v.toLocaleString('ru-RU');

export default function AdminAuction() {
  // ── Список ──
  const [auctions, setAuctions] = useState<AuctionListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterStatus>('all');

  // ── Форма аукциона ──
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<CreateAuctionRequest>({ ...emptyAuctionForm });
  const [submitLoading, setSubmitLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  // ── Детали аукциона (лоты) ──
  const [selected, setSelected] = useState<AuctionItem | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [showLotForm, setShowLotForm] = useState(false);
  const [editingLotId, setEditingLotId] = useState<number | null>(null);
  const [lotForm, setLotForm] = useState<LotFormState>({ ...emptyLotForm });
  const [lotSubmitLoading, setLotSubmitLoading] = useState(false);
  const [lotMessage, setLotMessage] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    auctionService
      .getAll(filter === 'all' ? undefined : filter)
      .then((res) => {
        if (res.success && res.data) setAuctions(res.data);
        else setError(res.message || 'Ошибка загрузки');
      })
      .catch(() => setError('Ошибка загрузки данных'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  // ── Аукцион: создание / редактирование ──

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyAuctionForm });
    setMessage(null);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const openEdit = (a: AuctionListItem | AuctionItem) => {
    // В списке нет описаний и ссылок — берём полную версию с сервера
    auctionService
      .getById(a.id)
      .then((res) => {
        if (!res.success || !res.data) return;
        const full = res.data;
        setEditingId(full.id);
        setForm({
          titleKz: full.titleKz,
          titleRu: full.titleRu,
          descriptionKz: full.descriptionKz ?? '',
          descriptionRu: full.descriptionRu ?? '',
          farmNameKz: full.farmNameKz,
          farmNameRu: full.farmNameRu,
          regionKz: full.regionKz,
          regionRu: full.regionRu,
          date: full.date,
          auctionType: full.auctionType,
          breed: full.breed,
          status: full.status,
          youtubeUrl: full.youtubeUrl ?? '',
          youtubeEmbedUrl: full.youtubeEmbedUrl ?? '',
          coverImage: full.coverImage ?? '',
          bannerImage: full.bannerImage ?? '',
        });
        setMessage(null);
        setShowForm(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      })
      .catch(() => {});
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm({ ...emptyAuctionForm });
    setMessage(null);
  };

  const handleChange = (field: keyof CreateAuctionRequest, value: string) => {
    setForm((f) => ({ ...f, [field]: value }));
    setMessage(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.titleRu.trim() || !form.titleKz.trim()) {
      setMessage({ type: 'err', text: 'Укажите название на русском и казахском' });
      return;
    }
    if (!form.farmNameRu.trim() || !form.farmNameKz.trim()) {
      setMessage({ type: 'err', text: 'Укажите хозяйство на русском и казахском' });
      return;
    }
    if (!form.regionRu.trim() || !form.regionKz.trim()) {
      setMessage({ type: 'err', text: 'Укажите регион на русском и казахском' });
      return;
    }
    if (!form.date) {
      setMessage({ type: 'err', text: 'Укажите дату проведения' });
      return;
    }
    if (!form.breed.trim()) {
      setMessage({ type: 'err', text: 'Укажите породу' });
      return;
    }

    const body: CreateAuctionRequest = {
      ...form,
      descriptionKz: form.descriptionKz?.trim() || null,
      descriptionRu: form.descriptionRu?.trim() || null,
      youtubeUrl: form.youtubeUrl?.trim() || null,
      youtubeEmbedUrl: form.youtubeEmbedUrl?.trim() || null,
      coverImage: form.coverImage?.trim() || null,
      bannerImage: form.bannerImage?.trim() || null,
    };

    setSubmitLoading(true);
    setMessage(null);
    const request = editingId
      ? auctionService.update(editingId, body)
      : auctionService.create(body);
    request
      .then((res) => {
        if (res.success) {
          closeForm();
          if (selected) reloadDetails();
          else load();
        } else {
          setMessage({ type: 'err', text: res.message || 'Ошибка сохранения' });
        }
      })
      .catch(() => setMessage({ type: 'err', text: 'Ошибка сохранения' }))
      .finally(() => setSubmitLoading(false));
  };

  const handleDelete = (a: AuctionListItem) => {
    if (!confirm(`Удалить аукцион «${a.titleRu}» вместе со всеми лотами?`)) return;
    auctionService
      .delete(a.id)
      .then((res) => {
        if (res.success) load();
        else alert(res.message || 'Ошибка удаления');
      })
      .catch(() => alert('Ошибка удаления'));
  };

  // ── Детали / лоты ──

  const openDetails = (id: number) => {
    setDetailsLoading(true);
    setShowLotForm(false);
    setEditingLotId(null);
    setLotForm({ ...emptyLotForm });
    setLotMessage(null);
    auctionService
      .getById(id)
      .then((res) => {
        if (res.success && res.data) setSelected(res.data);
      })
      .catch(() => {})
      .finally(() => setDetailsLoading(false));
  };

  const closeDetails = () => {
    setSelected(null);
    setShowLotForm(false);
    setEditingLotId(null);
    setLotMessage(null);
    load();
  };

  const reloadDetails = () => {
    if (selected) openDetails(selected.id);
  };

  const openLotCreate = () => {
    setEditingLotId(null);
    const nextNumber =
      selected && selected.lots.length > 0
        ? Math.max(...selected.lots.map((l) => l.lotNumber)) + 1
        : 1;
    setLotForm({ ...emptyLotForm, lotNumber: String(nextNumber) });
    setLotMessage(null);
    setShowLotForm(true);
  };

  const openLotEdit = (lot: AuctionLotItem) => {
    setEditingLotId(lot.id);
    setLotForm({
      lotNumber: String(lot.lotNumber),
      name: lot.name,
      inj: lot.inj,
      birthDate: lot.birthDate ?? '',
      ageMonths: lot.ageMonths != null ? String(lot.ageMonths) : '',
      birthWeight: lot.birthWeight != null ? String(lot.birthWeight) : '',
      weaningWeight: lot.weaningWeight != null ? String(lot.weaningWeight) : '',
      yearlingWeight: lot.yearlingWeight != null ? String(lot.yearlingWeight) : '',
      currentWeight: lot.currentWeight != null ? String(lot.currentWeight) : '',
      averageDailyGain: lot.averageDailyGain != null ? String(lot.averageDailyGain) : '',
      scrotalCircumference:
        lot.scrotalCircumference != null ? String(lot.scrotalCircumference) : '',
      spermQualityScore: lot.spermQualityScore != null ? String(lot.spermQualityScore) : '',
      heightAtWithers: lot.heightAtWithers != null ? String(lot.heightAtWithers) : '',
      line: lot.line ?? '',
      startingPrice: String(lot.startingPrice),
      imageUrl: lot.imageUrl ?? '',
    });
    setLotMessage(null);
    setShowLotForm(true);
  };

  const closeLotForm = () => {
    setShowLotForm(false);
    setEditingLotId(null);
    setLotForm({ ...emptyLotForm });
    setLotMessage(null);
  };

  const handleLotChange = (field: keyof LotFormState, value: string) => {
    setLotForm((f) => ({ ...f, [field]: value }));
    setLotMessage(null);
  };

  const handleLotSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selected) return;
    const lotNumber = toNumberOrNull(lotForm.lotNumber);
    if (!lotNumber || lotNumber < 1) {
      setLotMessage({ type: 'err', text: 'Укажите корректный номер лота' });
      return;
    }
    if (!lotForm.name.trim()) {
      setLotMessage({ type: 'err', text: 'Укажите кличку животного' });
      return;
    }
    if (!lotForm.inj.trim()) {
      setLotMessage({ type: 'err', text: 'Укажите ИНЖ' });
      return;
    }
    const startingPrice = toNumberOrNull(lotForm.startingPrice);
    if (startingPrice == null || startingPrice < 0) {
      setLotMessage({ type: 'err', text: 'Укажите корректную стартовую цену' });
      return;
    }

    const body: CreateAuctionLotRequest = {
      lotNumber,
      name: lotForm.name.trim(),
      inj: lotForm.inj.trim(),
      birthDate: lotForm.birthDate || null,
      ageMonths: toNumberOrNull(lotForm.ageMonths),
      birthWeight: toNumberOrNull(lotForm.birthWeight),
      weaningWeight: toNumberOrNull(lotForm.weaningWeight),
      yearlingWeight: toNumberOrNull(lotForm.yearlingWeight),
      currentWeight: toNumberOrNull(lotForm.currentWeight),
      averageDailyGain: toNumberOrNull(lotForm.averageDailyGain),
      scrotalCircumference: toNumberOrNull(lotForm.scrotalCircumference),
      spermQualityScore: toNumberOrNull(lotForm.spermQualityScore),
      heightAtWithers: toNumberOrNull(lotForm.heightAtWithers),
      line: lotForm.line.trim() || null,
      startingPrice,
      imageUrl: lotForm.imageUrl.trim() || null,
    };

    setLotSubmitLoading(true);
    setLotMessage(null);
    const request = editingLotId
      ? auctionService.updateLot(editingLotId, body)
      : auctionService.createLot(selected.id, body);
    request
      .then((res) => {
        if (res.success) {
          closeLotForm();
          reloadDetails();
        } else {
          setLotMessage({ type: 'err', text: res.message || 'Ошибка сохранения лота' });
        }
      })
      .catch(() => setLotMessage({ type: 'err', text: 'Ошибка сохранения лота' }))
      .finally(() => setLotSubmitLoading(false));
  };

  const handleLotDelete = (lot: AuctionLotItem) => {
    if (!confirm(`Удалить лот №${lot.lotNumber} «${lot.name}»?`)) return;
    auctionService
      .deleteLot(lot.id)
      .then((res) => {
        if (res.success) reloadDetails();
        else alert(res.message || 'Ошибка удаления лота');
      })
      .catch(() => alert('Ошибка удаления лота'));
  };

  // ── Рендер: детали аукциона ──

  if (selected || detailsLoading) {
    return (
      <div className="admin-auction">
        <button className="admin-auction-back-btn" onClick={closeDetails}>
          ← К списку аукционов
        </button>

        {detailsLoading || !selected ? (
          <p className="admin-auction-loading">Загрузка...</p>
        ) : (
          <>
            <div className="admin-auction-header">
              <div>
                <h1>{selected.titleRu}</h1>
                <p className="admin-auction-desc">
                  {selected.farmNameRu} · {selected.regionRu} · {formatDate(selected.date)} ·{' '}
                  {TYPE_LABELS[selected.auctionType]} · {selected.breed}
                  {'  '}
                  <span
                    className={`admin-auction-badge admin-auction-badge--${selected.status.toLowerCase()}`}
                  >
                    {STATUS_LABELS[selected.status]}
                  </span>
                </p>
              </div>
              <div className="admin-auction-header-actions">
                <button className="admin-auction-edit-btn" onClick={() => openEdit(selected)}>
                  Изменить аукцион
                </button>
                <button className="admin-auction-add-btn" onClick={openLotCreate}>
                  Добавить лот
                </button>
              </div>
            </div>

            {showForm && renderAuctionForm()}

            {showLotForm && (
              <form className="admin-auction-form" onSubmit={handleLotSubmit}>
                <h2 className="admin-auction-form-title">
                  {editingLotId ? 'Редактирование лота' : 'Новый лот'}
                </h2>
                <div className="admin-auction-form-grid admin-auction-form-grid--3">
                  <div className="admin-auction-row">
                    <label>Номер лота *</label>
                    <input
                      type="number"
                      min={1}
                      value={lotForm.lotNumber}
                      onChange={(e) => handleLotChange('lotNumber', e.target.value)}
                      required
                    />
                  </div>
                  <div className="admin-auction-row">
                    <label>Кличка *</label>
                    <input
                      type="text"
                      value={lotForm.name}
                      onChange={(e) => handleLotChange('name', e.target.value)}
                      required
                      maxLength={200}
                    />
                  </div>
                  <div className="admin-auction-row">
                    <label>ИНЖ *</label>
                    <input
                      type="text"
                      value={lotForm.inj}
                      onChange={(e) => handleLotChange('inj', e.target.value)}
                      required
                      maxLength={100}
                    />
                  </div>
                  <div className="admin-auction-row">
                    <label>Дата рождения</label>
                    <input
                      type="date"
                      value={lotForm.birthDate}
                      onChange={(e) => handleLotChange('birthDate', e.target.value)}
                    />
                  </div>
                  <div className="admin-auction-row">
                    <label>Возраст, мес.</label>
                    <input
                      type="number"
                      min={0}
                      value={lotForm.ageMonths}
                      onChange={(e) => handleLotChange('ageMonths', e.target.value)}
                    />
                  </div>
                  <div className="admin-auction-row">
                    <label>Линия</label>
                    <input
                      type="text"
                      value={lotForm.line}
                      onChange={(e) => handleLotChange('line', e.target.value)}
                      maxLength={200}
                    />
                  </div>
                  <div className="admin-auction-row">
                    <label>Вес при рождении, кг</label>
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      value={lotForm.birthWeight}
                      onChange={(e) => handleLotChange('birthWeight', e.target.value)}
                    />
                  </div>
                  <div className="admin-auction-row">
                    <label>Вес при отъёме, кг</label>
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      value={lotForm.weaningWeight}
                      onChange={(e) => handleLotChange('weaningWeight', e.target.value)}
                    />
                  </div>
                  <div className="admin-auction-row">
                    <label>Вес в 12 мес., кг</label>
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      value={lotForm.yearlingWeight}
                      onChange={(e) => handleLotChange('yearlingWeight', e.target.value)}
                    />
                  </div>
                  <div className="admin-auction-row">
                    <label>Текущий вес, кг</label>
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      value={lotForm.currentWeight}
                      onChange={(e) => handleLotChange('currentWeight', e.target.value)}
                    />
                  </div>
                  <div className="admin-auction-row">
                    <label>Среднесуточный привес, г</label>
                    <input
                      type="number"
                      min={0}
                      value={lotForm.averageDailyGain}
                      onChange={(e) => handleLotChange('averageDailyGain', e.target.value)}
                    />
                  </div>
                  <div className="admin-auction-row">
                    <label>Обхват мошонки, см</label>
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      value={lotForm.scrotalCircumference}
                      onChange={(e) => handleLotChange('scrotalCircumference', e.target.value)}
                    />
                  </div>
                  <div className="admin-auction-row">
                    <label>Оценка спермы (1-10)</label>
                    <input
                      type="number"
                      min={1}
                      max={10}
                      value={lotForm.spermQualityScore}
                      onChange={(e) => handleLotChange('spermQualityScore', e.target.value)}
                    />
                  </div>
                  <div className="admin-auction-row">
                    <label>Высота в холке, см</label>
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      value={lotForm.heightAtWithers}
                      onChange={(e) => handleLotChange('heightAtWithers', e.target.value)}
                    />
                  </div>
                  <div className="admin-auction-row">
                    <label>Стартовая цена, ₸ *</label>
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      value={lotForm.startingPrice}
                      onChange={(e) => handleLotChange('startingPrice', e.target.value)}
                      required
                    />
                  </div>
                  <div className="admin-auction-row admin-auction-row--full">
                    <label>Фото (URL)</label>
                    <input
                      type="url"
                      value={lotForm.imageUrl}
                      onChange={(e) => handleLotChange('imageUrl', e.target.value)}
                      maxLength={2000}
                    />
                  </div>
                </div>
                {lotMessage && (
                  <p
                    className={
                      lotMessage.type === 'ok' ? 'admin-auction-msg-ok' : 'admin-auction-msg-err'
                    }
                  >
                    {lotMessage.text}
                  </p>
                )}
                <div className="admin-auction-actions">
                  <button type="submit" disabled={lotSubmitLoading}>
                    {lotSubmitLoading
                      ? 'Сохранение...'
                      : editingLotId
                        ? 'Сохранить лот'
                        : 'Создать лот'}
                  </button>
                  <button type="button" className="admin-auction-cancel-btn" onClick={closeLotForm}>
                    Отмена
                  </button>
                </div>
              </form>
            )}

            <div className="admin-auction-table-wrap">
              <table className="admin-auction-table">
                <thead>
                  <tr>
                    <th>№</th>
                    <th>Кличка</th>
                    <th>ИНЖ</th>
                    <th>Дата рожд.</th>
                    <th>Возраст</th>
                    <th>Вес, кг</th>
                    <th>Линия</th>
                    <th>Старт. цена, ₸</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {selected.lots.map((lot) => (
                    <tr key={lot.id}>
                      <td>{lot.lotNumber}</td>
                      <td>{lot.name}</td>
                      <td>{lot.inj}</td>
                      <td>{lot.birthDate ? formatDate(lot.birthDate) : '—'}</td>
                      <td>{lot.ageMonths != null ? `${lot.ageMonths} мес.` : '—'}</td>
                      <td>{lot.currentWeight ?? '—'}</td>
                      <td>{lot.line ?? '—'}</td>
                      <td>{formatPrice(lot.startingPrice)}</td>
                      <td>
                        <div className="admin-auction-row-actions">
                          <button
                            className="admin-auction-edit-btn"
                            onClick={() => openLotEdit(lot)}
                          >
                            Изменить
                          </button>
                          <button
                            className="admin-auction-del-btn"
                            onClick={() => handleLotDelete(lot)}
                          >
                            Удалить
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {selected.lots.length === 0 && (
                <p className="admin-auction-empty">В аукционе пока нет лотов</p>
              )}
            </div>
          </>
        )}
      </div>
    );
  }

  // ── Рендер: форма аукциона (используется в списке и в деталях) ──

  function renderAuctionForm() {
    return (
      <form className="admin-auction-form" onSubmit={handleSubmit}>
        <h2 className="admin-auction-form-title">
          {editingId ? 'Редактирование аукциона' : 'Новый аукцион'}
        </h2>
        <div className="admin-auction-form-grid">
          <div className="admin-auction-row">
            <label>Название (рус) *</label>
            <input
              type="text"
              value={form.titleRu}
              onChange={(e) => handleChange('titleRu', e.target.value)}
              required
              maxLength={500}
            />
          </div>
          <div className="admin-auction-row">
            <label>Название (каз) *</label>
            <input
              type="text"
              value={form.titleKz}
              onChange={(e) => handleChange('titleKz', e.target.value)}
              required
              maxLength={500}
            />
          </div>
          <div className="admin-auction-row admin-auction-row--full">
            <label>Описание (рус)</label>
            <textarea
              rows={3}
              value={form.descriptionRu ?? ''}
              onChange={(e) => handleChange('descriptionRu', e.target.value)}
              maxLength={4000}
            />
          </div>
          <div className="admin-auction-row admin-auction-row--full">
            <label>Описание (каз)</label>
            <textarea
              rows={3}
              value={form.descriptionKz ?? ''}
              onChange={(e) => handleChange('descriptionKz', e.target.value)}
              maxLength={4000}
            />
          </div>
          <div className="admin-auction-row">
            <label>Хозяйство (рус) *</label>
            <input
              type="text"
              value={form.farmNameRu}
              onChange={(e) => handleChange('farmNameRu', e.target.value)}
              required
              maxLength={500}
            />
          </div>
          <div className="admin-auction-row">
            <label>Хозяйство (каз) *</label>
            <input
              type="text"
              value={form.farmNameKz}
              onChange={(e) => handleChange('farmNameKz', e.target.value)}
              required
              maxLength={500}
            />
          </div>
          <div className="admin-auction-row">
            <label>Регион (рус) *</label>
            <input
              type="text"
              value={form.regionRu}
              onChange={(e) => handleChange('regionRu', e.target.value)}
              required
              maxLength={200}
            />
          </div>
          <div className="admin-auction-row">
            <label>Регион (каз) *</label>
            <input
              type="text"
              value={form.regionKz}
              onChange={(e) => handleChange('regionKz', e.target.value)}
              required
              maxLength={200}
            />
          </div>
          <div className="admin-auction-row">
            <label>Дата проведения *</label>
            <input
              type="date"
              value={form.date}
              onChange={(e) => handleChange('date', e.target.value)}
              required
            />
          </div>
          <div className="admin-auction-row">
            <label>Порода *</label>
            <input
              type="text"
              value={form.breed}
              onChange={(e) => handleChange('breed', e.target.value)}
              required
              maxLength={200}
            />
          </div>
          <div className="admin-auction-row">
            <label>Тип проведения *</label>
            <select
              value={form.auctionType}
              onChange={(e) => handleChange('auctionType', e.target.value)}
            >
              {(Object.keys(TYPE_LABELS) as AuctionType[]).map((t) => (
                <option key={t} value={t}>
                  {TYPE_LABELS[t]}
                </option>
              ))}
            </select>
          </div>
          <div className="admin-auction-row">
            <label>Статус *</label>
            <select value={form.status} onChange={(e) => handleChange('status', e.target.value)}>
              {(Object.keys(STATUS_LABELS) as AuctionStatus[]).map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABELS[s]}
                </option>
              ))}
            </select>
          </div>
          <div className="admin-auction-row">
            <label>YouTube ссылка</label>
            <input
              type="url"
              value={form.youtubeUrl ?? ''}
              onChange={(e) => handleChange('youtubeUrl', e.target.value)}
              maxLength={1000}
            />
          </div>
          <div className="admin-auction-row">
            <label>YouTube embed ссылка</label>
            <input
              type="url"
              value={form.youtubeEmbedUrl ?? ''}
              onChange={(e) => handleChange('youtubeEmbedUrl', e.target.value)}
              maxLength={1000}
            />
          </div>
          <div className="admin-auction-row">
            <label>Обложка (URL)</label>
            <input
              type="url"
              value={form.coverImage ?? ''}
              onChange={(e) => handleChange('coverImage', e.target.value)}
              maxLength={2000}
            />
          </div>
          <div className="admin-auction-row">
            <label>Баннер (URL)</label>
            <input
              type="url"
              value={form.bannerImage ?? ''}
              onChange={(e) => handleChange('bannerImage', e.target.value)}
              maxLength={2000}
            />
          </div>
        </div>
        {message && (
          <p className={message.type === 'ok' ? 'admin-auction-msg-ok' : 'admin-auction-msg-err'}>
            {message.text}
          </p>
        )}
        <div className="admin-auction-actions">
          <button type="submit" disabled={submitLoading}>
            {submitLoading
              ? 'Сохранение...'
              : editingId
                ? 'Сохранить аукцион'
                : 'Создать аукцион'}
          </button>
          <button type="button" className="admin-auction-cancel-btn" onClick={closeForm}>
            Отмена
          </button>
        </div>
      </form>
    );
  }

  // ── Рендер: список аукционов ──

  return (
    <div className="admin-auction">
      <div className="admin-auction-header">
        <div>
          <h1>Аукционы</h1>
          <p className="admin-auction-desc">Управление аукционами и лотами</p>
        </div>
        <button className="admin-auction-add-btn" onClick={openCreate}>
          Создать аукцион
        </button>
      </div>

      <div className="admin-auction-filters">
        {(['all', 'Upcoming', 'Active', 'Completed'] as FilterStatus[]).map((s) => (
          <button
            key={s}
            className={`admin-auction-filter ${filter === s ? 'admin-auction-filter--active' : ''}`}
            onClick={() => setFilter(s)}
          >
            {s === 'all' ? 'Все' : STATUS_LABELS[s]}
          </button>
        ))}
      </div>

      {showForm && renderAuctionForm()}

      {loading ? (
        <p className="admin-auction-loading">Загрузка...</p>
      ) : error ? (
        <p className="admin-auction-error">{error}</p>
      ) : (
        <div className="admin-auction-table-wrap">
          <table className="admin-auction-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Название</th>
                <th>Хозяйство</th>
                <th>Регион</th>
                <th>Дата</th>
                <th>Тип</th>
                <th>Порода</th>
                <th>Статус</th>
                <th>Лотов</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {auctions.map((a) => (
                <tr key={a.id}>
                  <td>{a.id}</td>
                  <td>{a.titleRu}</td>
                  <td>{a.farmNameRu}</td>
                  <td>{a.regionRu}</td>
                  <td>{formatDate(a.date)}</td>
                  <td>{TYPE_LABELS[a.auctionType]}</td>
                  <td>{a.breed}</td>
                  <td>
                    <span
                      className={`admin-auction-badge admin-auction-badge--${a.status.toLowerCase()}`}
                    >
                      {STATUS_LABELS[a.status]}
                    </span>
                  </td>
                  <td>{a.lotsCount}</td>
                  <td>
                    <div className="admin-auction-row-actions">
                      <button className="admin-auction-open-btn" onClick={() => openDetails(a.id)}>
                        Открыть
                      </button>
                      <button className="admin-auction-edit-btn" onClick={() => openEdit(a)}>
                        Изменить
                      </button>
                      <button className="admin-auction-del-btn" onClick={() => handleDelete(a)}>
                        Удалить
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {auctions.length === 0 && <p className="admin-auction-empty">Нет аукционов</p>}
        </div>
      )}
    </div>
  );
}
